"use client";

import * as React from "react";
import { cn } from "@baluu/ui/lib/utils";

type ResolveTypedQuantityArgs = {
  /** What the customer typed (digits only, may be empty). */
  raw: string;
  /** The quantity currently held for this ticket. */
  current: number;
  minQuantity: number;
  maxQuantity: number;
  /** Mirrors the selector's "+" gate (availability, membership, eligibility). */
  canIncrease: boolean;
  /** Mirrors the selector's "−" gate. */
  canDecrease: boolean;
};

type ResolvedQuantity = {
  quantity: number;
  /** Shown under the stepper when the typed value had to be adjusted. */
  note?: string;
};

/**
 * Turns a typed ticket quantity into one the stepper could have reached.
 *
 * The rules match the −/+ buttons so typing can never produce a state the
 * buttons couldn't: an empty field or 0 removes the ticket, a value under the
 * per-order minimum is raised to the minimum (1..min-1 is never bookable), a
 * value over the cap is lowered to it, and a row whose "+" is blocked (sold
 * out, members-only, ineligible) can only be reduced.
 */
export function resolveTypedTicketQuantity({
  raw,
  current,
  minQuantity,
  maxQuantity,
  canIncrease,
  canDecrease,
}: ResolveTypedQuantityArgs): ResolvedQuantity {
  const trimmed = raw.trim();
  if (trimmed !== "" && !/^\d+$/.test(trimmed)) return { quantity: current };

  let target = trimmed === "" ? 0 : parseInt(trimmed, 10);
  if (target === current) return { quantity: current };

  if (target > current && !canIncrease) {
    return {
      quantity: current,
      note: "No more of this ticket can be added",
    };
  }
  if (target < current && !canDecrease) return { quantity: current };

  let note: string | undefined;
  if (target > maxQuantity) {
    target = Math.max(maxQuantity, 0);
    note = `You can book up to ${target}`;
  }
  if (target > 0 && target < minQuantity) {
    if (minQuantity > maxQuantity) {
      return {
        quantity: 0,
        note: `Not enough availability for the minimum of ${minQuantity}`,
      };
    }
    target = minQuantity;
    note = `Minimum ${minQuantity} per order`;
  }

  return { quantity: target, note };
}

type TicketQuantityInputProps = {
  ticketName: string;
  quantity: number;
  minQuantity: number;
  maxQuantity: number;
  canIncrease: boolean;
  canDecrease: boolean;
  onChange: (quantity: number) => void;
  /** Receives the adjustment message (or undefined) after each commit. */
  onNote: (note: string | undefined) => void;
  className?: string;
};

/**
 * The editable number between a ticket's −/+ buttons, so a group booking of 8
 * is typed rather than tapped eight times.
 *
 * Holds the raw text while focused (so it can be cleared and retyped) and
 * commits on blur or Enter — the same pattern as ClearableNumberInput, but
 * with the stepper's own rules (see resolveTypedTicketQuantity), which a plain
 * min/max clamp can't express: 0 must stay reachable below a minimum of 8.
 * type="text" + inputMode="numeric" brings up the numeric keypad on mobile
 * without the browser spinner or scroll-wheel changes of type="number".
 */
export function TicketQuantityInput({
  ticketName,
  quantity,
  minQuantity,
  maxQuantity,
  canIncrease,
  canDecrease,
  onChange,
  onNote,
  className,
}: TicketQuantityInputProps) {
  const [text, setText] = React.useState(() => String(quantity));
  const [focused, setFocused] = React.useState(false);
  const [lastQuantity, setLastQuantity] = React.useState(quantity);
  // Escape blurs to cancel; the blur handler still sees the typed text.
  const cancelledRef = React.useRef(false);

  // Follow −/+ presses and parent-driven changes without clobbering typing.
  if (!focused && quantity !== lastQuantity) {
    setLastQuantity(quantity);
    setText(String(quantity));
  }

  const commit = () => {
    const { quantity: next, note } = resolveTypedTicketQuantity({
      raw: text,
      current: quantity,
      minQuantity,
      maxQuantity,
      canIncrease,
      canDecrease,
    });
    setText(String(next));
    onNote(note);
    if (next !== quantity) onChange(next);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      autoComplete="off"
      enterKeyHint="done"
      aria-label={`${ticketName} quantity`}
      disabled={!canIncrease && !canDecrease}
      value={text}
      onChange={(e) => setText(e.target.value.replace(/\D/g, "").slice(0, 4))}
      onFocus={(e) => {
        setFocused(true);
        e.currentTarget.select();
      }}
      onBlur={() => {
        setFocused(false);
        if (cancelledRef.current) {
          cancelledRef.current = false;
          setText(String(quantity));
          return;
        }
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          cancelledRef.current = true;
          e.currentTarget.blur();
        }
      }}
      className={cn(
        "w-12 h-10 rounded-md border border-border/60 bg-transparent text-center text-base font-medium",
        "outline-none transition-all duration-200",
        "hover:border-border focus:border-ring focus:ring-[3px] focus:ring-ring/50",
        "disabled:cursor-not-allowed",
        quantity > 0 ? "text-primary text-lg font-bold" : "text-foreground",
        className,
      )}
    />
  );
}
